import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Card, ListGroup, Badge, Button, Spinner, Alert, Modal, Accordion } from 'react-bootstrap';
import { FaCheckCircle, FaFlask, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { getPlantillasProcedimiento, postLotesCalibracionBulk } from '../../../../api/common';
import PlantillaForm from '../../plantillas/PlantillaForm';
import { SelectMultPatron } from '../../../Items/SelectMultPatron';
import { Alertassw } from '../../../Alertas/Alertassw';

/**
 * Input numérico reusable para Step 1 (reemplaza `<Form.Control type="number">`).
 *   - Sin flechas de spin (type="text" + inputMode="decimal").
 *   - Scroll NO modifica valor (onWheel → blur).
 *   - Permite estado vacío (`''`) para poder borrar y re-escribir sin arrastrar
 *     caracteres previos. onChange recibe el valor normalizado (número o '').
 *   - Si se pasa `min`/`max`, se enforza en el commit (al dejar de tipear).
 */
const NumericInput = ({ value, onChange, min, max, disabled, size = 'sm', placeholder, allowDecimals = true, className = '' }) => {
  const display = (value === '' || value === null || value === undefined) ? '' : String(value);
  const regex = allowDecimals ? /^\d*\.?\d*$/ : /^\d*$/;

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') { onChange(''); return; }
    if (!regex.test(raw)) return; // ignora caracteres inválidos
    // Si termina con '.' (user tipeando decimal), dejar la string tal cual.
    if (raw.endsWith('.')) { onChange(raw); return; }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    if (typeof max === 'number' && num > max) return;
    if (typeof min === 'number' && num < min) return;
    onChange(num);
  };

  return (
    <Form.Control
      type="text"
      inputMode={allowDecimals ? 'decimal' : 'numeric'}
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onWheel={(e) => e.currentTarget.blur()}
      disabled={disabled}
      size={size}
      placeholder={placeholder}
      className={className}
    />
  );
};

const StepSeleccionPlantilla = ({ loteEquipos, onLotesCreados }) => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plantillaSelected, setPlantillaSelected] = useState(null);
  const [magnitudesPersonalizadas, setMagnitudesPersonalizadas] = useState([]);
  // Override per-lote de la cantidad de patrones requeridos — Step 1 permite
  // personalizar sin modificar la plantilla original.
  const [cantPatronesRequeridos, setCantPatronesRequeridos] = useState(0);
  const [creandoLotes, setCreandoLotes] = useState(false);
  const [error, setError] = useState(null);
  const [showModalCrearPlantilla, setShowModalCrearPlantilla] = useState(false);

  console.log('plantillaSelected:', plantillaSelected); 
  console.log('magnitudesPersonalizadas:', magnitudesPersonalizadas); 
  useEffect(() => {
    cargarPlantillas();
  }, []);

  const cargarPlantillas = async () => {
    try {
      const res = await getPlantillasProcedimiento();
      const payload = res?.data?.data ?? res?.data ?? [];
      const activas = Array.isArray(payload) ? payload.filter(p => p.activa !== false) : [];
      setPlantillas(activas);
    } catch (err) {
      console.error('Error cargando plantillas:', err);
      setError('Error al cargar las plantillas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handlePlantillaChange = (e) => {
    const id = e.target.value;
    if (id === 'CREAR_NUEVA') {
      setShowModalCrearPlantilla(true);
      return;
    }
    const plantilla = plantillas.find(p => p._id === id);
    setPlantillaSelected(plantilla);
    
    // Inicializar magnitudes personalizadas con los datos de la plantilla
    // Normalizar patronesCalibracion y medioCalibracion para asegurar que siempre sean arrays de IDs
    if (plantilla && plantilla.magnitudesConfig) {
      const magnitudesNormalizadas = JSON.parse(JSON.stringify(plantilla.magnitudesConfig)).map(m => {
        // Normalizar patronesCalibracion
        let patronesArray = [];
        if (Array.isArray(m.patronesCalibracion)) {
          // Si es array, convertir cada elemento a string ID
          patronesArray = m.patronesCalibracion.map(p => 
            typeof p === 'string' ? p : p._id || p.id || p
          );
        } else if (typeof m.patronesCalibracion === 'string' && m.patronesCalibracion) {
          // Si es string, convertir a array con un elemento
          patronesArray = [m.patronesCalibracion];
        } else if (m.patronesCalibracion && typeof m.patronesCalibracion === 'object' && m.patronesCalibracion._id) {
          // Si es objeto poblado, extraer el _id
          patronesArray = [m.patronesCalibracion._id];
        }
        
        // Normalizar medioCalibracion
        let medioArray = [];
        if (Array.isArray(m.medioCalibracion)) {
          // Si es array, convertir cada elemento a string ID
          medioArray = m.medioCalibracion.map(p => 
            typeof p === 'string' ? p : p._id || p.id || p
          );
        } else if (typeof m.medioCalibracion === 'string' && m.medioCalibracion) {
          // Si es string, convertir a array con un elemento
          medioArray = [m.medioCalibracion];
        } else if (m.medioCalibracion && typeof m.medioCalibracion === 'object' && m.medioCalibracion._id) {
          // Si es objeto poblado, extraer el _id
          medioArray = [m.medioCalibracion._id];
        }
        
        return {
          ...m,
          patronesCalibracion: patronesArray,
          medioCalibracion: medioArray,
          requiereMedioCalibracion: m.requiereMedioCalibracion || false
        };
      });
      setMagnitudesPersonalizadas(magnitudesNormalizadas);
      // Inicializar override de cantPatronesRequeridos con el valor de la plantilla.
      setCantPatronesRequeridos(Number(plantilla.cantPatronesRequeridos) || 0);
    }
  };

  const handleMagnitudPersonalizadaChange = (idx, key, value) => {
    const copy = [...magnitudesPersonalizadas];
    copy[idx] = { ...copy[idx], [key]: value };

    // Si cambia cantPuntos, ajustar array de puntosCalibracion — PERO solo si
    // value es un número válido. Durante edición puede ser '' (campo borrado
    // temporalmente) y no queremos vaciar el array hasta que el usuario termine.
    if (key === 'cantPuntos' && value !== '' && !Number.isNaN(Number(value))) {
      const newSize = Number(value);
      const currentPoints = copy[idx].puntosCalibracion || [];
      if (newSize > currentPoints.length) {
        copy[idx].puntosCalibracion = [...currentPoints, ...Array(newSize - currentPoints.length).fill('')];
      } else {
        copy[idx].puntosCalibracion = currentPoints.slice(0, newSize);
      }
    }

    setMagnitudesPersonalizadas(copy);
  };

  const handlePuntoCalibracionChange = (magnitudIdx, puntoIdx, value) => {
    const copy = [...magnitudesPersonalizadas];
    if (!copy[magnitudIdx].puntosCalibracion) {
      copy[magnitudIdx].puntosCalibracion = [];
    }
    copy[magnitudIdx].puntosCalibracion[puntoIdx] = value === '' ? '' : Number(value);
    setMagnitudesPersonalizadas(copy);
  };

  const handlemedioCalibracionChange = (magnitudIdx, event) => {
    const copy = [...magnitudesPersonalizadas];
    const value = event.target.value;
    // Asegurar que siempre sea un array de strings
    copy[magnitudIdx].medioCalibracion = Array.isArray(value) ? value : [];
    setMagnitudesPersonalizadas(copy);
  };

  const handlePatronesCalibracionChange = (magnitudIdx, event) => {
    const copy = [...magnitudesPersonalizadas];
    const value = event.target.value;
    const seleccionada = Array.isArray(value) ? value : [];
    const previa = Array.isArray(copy[magnitudIdx].patronesCalibracion)
      ? copy[magnitudIdx].patronesCalibracion
      : [];
    // Enforce límite: si el usuario intenta pasar `cantPatronesRequeridos`,
    // ignoramos la adición y avisamos. Deseleccionar siempre es permitido
    // (`seleccionada.length <= previa.length`).
    if (
      cantPatronesRequeridos > 0
      && seleccionada.length > cantPatronesRequeridos
      && seleccionada.length > previa.length
    ) {
      Alertassw(
        'error',
        `Máximo ${cantPatronesRequeridos} patrón(es) por magnitud`,
        'Quita uno antes de agregar otro.'
      );
      return;
    }
    copy[magnitudIdx].patronesCalibracion = seleccionada;
    setMagnitudesPersonalizadas(copy);
  };

  // Validaciones antes de crear los lotes:
  //   - cantPatronesRequeridos definido (número >= 0).
  //   - Por cada magnitud: cantPuntos, cantMedicionesPorPunto, y
  //     cantMedicionesEstabilidad (si aplica) definidos y >= 1.
  //   - patronesCalibracion.length === cantPatronesRequeridos por magnitud.
  const erroresPatrones = React.useMemo(() => {
    if (!plantillaSelected) return [];
    const errores = [];

    if (cantPatronesRequeridos === '' || cantPatronesRequeridos === null || cantPatronesRequeridos === undefined) {
      errores.push('Debes definir la cantidad de patrones requeridos.');
    }

    (magnitudesPersonalizadas || []).forEach((m) => {
      // Numéricos obligatorios
      if (m.cantPuntos === '' || m.cantPuntos === undefined || Number(m.cantPuntos) < 1) {
        errores.push(`Magnitud "${m.nombre}": cantidad de puntos debe ser >= 1.`);
      }
      if (m.cantMedicionesPorPunto === '' || m.cantMedicionesPorPunto === undefined || Number(m.cantMedicionesPorPunto) < 1) {
        errores.push(`Magnitud "${m.nombre}": mediciones por punto debe ser >= 1.`);
      }
      if (m.requiereEstabilidad && (m.cantMedicionesEstabilidad === '' || m.cantMedicionesEstabilidad === undefined || Number(m.cantMedicionesEstabilidad) < 1)) {
        errores.push(`Magnitud "${m.nombre}": mediciones de estabilidad debe ser >= 1.`);
      }
      // Patrones
      const sel = Array.isArray(m.patronesCalibracion) ? m.patronesCalibracion.length : 0;
      if (typeof cantPatronesRequeridos === 'number' && sel !== cantPatronesRequeridos) {
        errores.push(
          `Magnitud "${m.nombre}": ${sel} de ${cantPatronesRequeridos} patrón(es) seleccionado(s).`
        );
      }
    });
    return errores;
  }, [plantillaSelected, magnitudesPersonalizadas, cantPatronesRequeridos]);
  const formularioValido = erroresPatrones.length === 0;

const handleCrearLotes = async () => {
    if (!plantillaSelected) return;
    // Guard final — también se bloquea el botón, pero por si llega por otra ruta.
    if (!formularioValido) {
      setError('Hay magnitudes con cantidad de patrones incorrecta. Revisa los mensajes bajo cada magnitud.');
      return;
    }

    setCreandoLotes(true);
    setError(null);
    try {
      const equiposIds = (loteEquipos.equipos || []).map(eq => eq._id);
      
      const body = {
        loteEquipos: loteEquipos._id,
        plantillaId: plantillaSelected._id,
        equipos: equiposIds,
        responsable: loteEquipos.responsable?._id,
        magnitudesPersonalizadas: magnitudesPersonalizadas,
        // Override per-lote de cantPatronesRequeridos.
        cantPatronesRequeridos: cantPatronesRequeridos,
      };

      const res = await postLotesCalibracionBulk(body);

      console.log('Lotes creados:', res);
      const lotesCreados = res?.data?.data ?? res?.data ?? [];

      // Pasar plantilla con magnitudes personalizadas Y cantPatronesRequeridos
      // override — así los steps siguientes ven la personalización completa aun
      // sin que recarguemos los lotes de la DB.
      const plantillaConPersonalizacion = {
        ...plantillaSelected,
        magnitudesConfig: magnitudesPersonalizadas,
        cantPatronesRequeridos: cantPatronesRequeridos,
      };
      
      onLotesCreados(lotesCreados, plantillaConPersonalizacion);
    } catch (err) {
      console.error('Error creando lotes:', err);
      setError('Error al crear los lotes de calibración. Por favor, intenta nuevamente.');
    } finally {
      setCreandoLotes(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando plantillas disponibles...</p>
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-4">Paso 1: Seleccionar y personalizar plantilla</h5>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={12}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Plantilla de procedimiento</Form.Label>
            <Form.Select
              value={plantillaSelected?._id || ''}
              onChange={handlePlantillaChange}
              disabled={creandoLotes}
              size="lg"
            >
              <option value="">-- Selecciona una plantilla --</option>
              <option value="CREAR_NUEVA" className="fw-bold text-primary">+ Crear nueva plantilla</option>
              <option disabled>──────────</option>
              {plantillas.map(p => (
                <option key={p._id} value={p._id}>
                  {p.codigo} - {p.descripcion || p.nombre}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Total de equipos en este lote: <strong>{(loteEquipos.equipos || []).length}</strong>
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {plantillaSelected && (
        <>
          {/* Resumen de la plantilla */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm w-100" style={{borderRadius: '12px'}}>
                <Card.Header className="bg-primary text-white w-100" style={{borderRadius: '12px 12px 0 0'}}>
                  <FaFlask className="me-2" /> Resumen de la plantilla
                </Card.Header>
                <Card.Body className="w-100">
                  <Row>
                    <Col md={3}>
                      <small className="text-muted d-block">Código</small>
                      <strong>{plantillaSelected.codigo}</strong>
                    </Col>
                    <Col md={6}>
                      <small className="text-muted d-block">Descripción</small>
                      <strong>{plantillaSelected.descripcion}</strong>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">
                          Patrones requeridos
                          <span className="ms-1" title="Puedes personalizar la cantidad para este lote sin modificar la plantilla original.">⓵</span>
                        </Form.Label>
                        <NumericInput
                          allowDecimals={false}
                          value={cantPatronesRequeridos}
                          onChange={(v) => setCantPatronesRequeridos(v)}
                          disabled={creandoLotes}
                        />
                        <Form.Text className="text-muted" style={{fontSize: '0.7rem'}}>
                          Plantilla: {plantillaSelected.cantPatronesRequeridos}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Magnitudes personalizables */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm w-100 mb-4" style={{borderRadius: '12px'}}>
                <Card.Header className="bg-secondary text-white w-100" style={{borderRadius: '12px 12px 0 0'}}>
                  <FaEdit className="me-2" /> Personalizar Magnitudes
                  <small className="ms-2">(opcional - puedes editar antes de crear lotes)</small>
                </Card.Header>
                <Card.Body className="w-100">
                  <Accordion defaultActiveKey="0">
                    {magnitudesPersonalizadas.map((m, idx) => (
                      <Accordion.Item eventKey={idx.toString()} key={idx}>
                        <Accordion.Header>
                          <strong>{m.nombre || `Magnitud ${idx + 1}`}</strong>
                          <Badge bg="info" className="ms-2">{m.unidad}</Badge>
                          <Badge bg="secondary" className="ms-2">{m.cantPuntos} puntos</Badge>
                          {m.requiereHisteresis && <Badge bg="warning" className="ms-2">Histéresis</Badge>}
                          {m.requiereEstabilidad && <Badge bg="success" className="ms-2">Estabilidad</Badge>}
                          {m.requiereMedioCalibracion && <Badge bg="primary" className="ms-2">Medio Calib.</Badge>}
                          {m.patronesCalibracion?.length > 0 && <Badge bg="dark" className="ms-2">{m.patronesCalibracion.length} patrones</Badge>}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>Nombre</Form.Label>
                                <Form.Control 
                                  value={m.nombre} 
                                  onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'nombre', e.target.value)} 
                                />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label>Unidad</Form.Label>
                                <Form.Control 
                                  value={m.unidad} 
                                  onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'unidad', e.target.value)} 
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Cantidad de puntos</Form.Label>
                                <NumericInput
                                  allowDecimals={false}
                                  size="md"
                                  value={m.cantPuntos}
                                  onChange={(v) => handleMagnitudPersonalizadaChange(idx, 'cantPuntos', v)}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Mediciones por punto</Form.Label>
                                <NumericInput
                                  allowDecimals={false}
                                  size="md"
                                  value={m.cantMedicionesPorPunto}
                                  onChange={(v) => handleMagnitudPersonalizadaChange(idx, 'cantMedicionesPorPunto', v)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* Puntos de calibración */}
                          <Row className="mb-3">
                            <Col>
                              <Form.Label className="fw-bold">Puntos de Calibración (valores)</Form.Label>
                              <Row>
                                {(m.puntosCalibracion || []).map((punto, pIdx) => (
                                  <Col md={3} key={pIdx} className="mb-2">
                                    <Form.Group>
                                      <Form.Label className="small">Punto {pIdx + 1}</Form.Label>
                                      <NumericInput
                                        size="md"
                                        value={punto === 0 ? 0 : (punto || '')}
                                        onChange={(v) => handlePuntoCalibracionChange(idx, pIdx, v)}
                                        placeholder={`Valor punto ${pIdx + 1}`}
                                      />
                                    </Form.Group>
                                  </Col>
                                ))}
                                {m.requiereHisteresis && !m.histeresisEnTodosLosPuntos && (
                                  <Col md={3} className="mb-2">
                                    <Form.Group>
                                      <Form.Label className="small">Punto Histéresis</Form.Label>
                                      <NumericInput
                                        size="md"
                                        value={m.puntoHisteresis === 0 ? 0 : (m.puntoHisteresis || '')}
                                        onChange={(v) => handleMagnitudPersonalizadaChange(idx, 'puntoHisteresis', v)}
                                        placeholder="Punto histéresis"
                                      />
                                    </Form.Group>
                                  </Col>
                                )}
                              </Row>
                            </Col>
                          </Row>

                          {/* Opciones adicionales */}
                          <Row className="mb-3">
                            <Col md={3}>
                              <Form.Check 
                                type="switch" 
                                label="Requiere histéresis" 
                                checked={m.requiereHisteresis || false} 
                                onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'requiereHisteresis', e.target.checked)} 
                              />
                            </Col>
                            {m.requiereHisteresis && (
                              <Col md={3}>
                                <Form.Check 
                                  type="switch" 
                                  label="En todos los puntos" 
                                  checked={m.histeresisEnTodosLosPuntos || false} 
                                  onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'histeresisEnTodosLosPuntos', e.target.checked)} 
                                />
                              </Col>
                            )}
                            <Col md={3}>
                              <Form.Check 
                                type="switch" 
                                label="Requiere estabilidad" 
                                checked={m.requiereEstabilidad || false} 
                                onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'requiereEstabilidad', e.target.checked)} 
                              />
                            </Col>
                            {m.requiereEstabilidad && (
                              <Col md={3}>
                                <Form.Group>
                                  <Form.Label className="small">Mediciones est.</Form.Label>
                                  <NumericInput
                                    allowDecimals={false}
                                    value={m.cantMedicionesEstabilidad ?? ''}
                                    onChange={(v) => handleMagnitudPersonalizadaChange(idx, 'cantMedicionesEstabilidad', v)}
                                    placeholder="10"
                                  />
                                </Form.Group>
                              </Col>
                            )}
                          </Row>

                          {/* Medio de calibración */}
                          <Row className="mb-3">
                            <Col md={12}>
                              <Form.Check 
                                type="switch" 
                                label="Requiere medio de calibración" 
                                checked={m.requiereMedioCalibracion || false} 
                                onChange={(e) => handleMagnitudPersonalizadaChange(idx, 'requiereMedioCalibracion', e.target.checked)} 
                              />
                            </Col>
                            {m.requiereMedioCalibracion && (
                              <Col md={12} className="mt-2">
                                <Form.Group>
                                  <Form.Label>Patrones para medio de calibración</Form.Label>
                                  <SelectMultPatron
                                    key={`medio_${idx}_${JSON.stringify(m.medioCalibracion)}`}
                                    dataDefault={
                                      Array.isArray(m.medioCalibracion) 
                                        ? m.medioCalibracion.map(p => typeof p === 'string' ? p : p._id || p.id || p)
                                        : (m.medioCalibracion && typeof m.medioCalibracion === 'object' && m.medioCalibracion._id)
                                          ? [m.medioCalibracion._id]
                                          : (typeof m.medioCalibracion === 'string' && m.medioCalibracion) 
                                            ? [m.medioCalibracion] 
                                            : []
                                    }
                                    handledata={(event) => handlemedioCalibracionChange(idx, event)}
                                    name={`medioCalibracion_${idx}`}
                                    collection={"Patrones"}
                                    field={"item"}
                                    excludeIds={[]}
                                  />
                                  <Form.Text className="text-muted">
                                    Máximo {cantPatronesRequeridos} patrón(es) - Seleccionados: {Array.isArray(m.medioCalibracion) ? m.medioCalibracion.length : (m.medioCalibracion ? 1 : 0)}
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            )}
                          </Row>

                          {/* Patrones de calibración */}
                          <Row className="mb-3">
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Patrones de Calibración</Form.Label>
                                <SelectMultPatron
                                  key={`patrones_${idx}_${JSON.stringify(m.patronesCalibracion)}`}
                                  dataDefault={
                                    Array.isArray(m.patronesCalibracion) 
                                      ? m.patronesCalibracion.map(p => typeof p === 'string' ? p : p._id || p.id || p)
                                      : (m.patronesCalibracion && typeof m.patronesCalibracion === 'object' && m.patronesCalibracion._id)
                                        ? [m.patronesCalibracion._id]
                                        : (typeof m.patronesCalibracion === 'string' && m.patronesCalibracion) 
                                          ? [m.patronesCalibracion] 
                                          : []
                                  }
                                  handledata={(event) => handlePatronesCalibracionChange(idx, event)}
                                  name={`patronesCalibracion_${idx}`}
                                  collection={"Patrones"}
                                  field={"item"}
                                  excludeIds={[]}
                                />
                                <Form.Text className="text-muted">
                                  Máximo {cantPatronesRequeridos} patrón(es) - Seleccionados: {Array.isArray(m.patronesCalibracion) ? m.patronesCalibracion.length : (m.patronesCalibracion ? 1 : 0)}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {plantillaSelected && (
        <div className="text-center mt-4">
          <Button
            variant="success"
            size="lg"
            onClick={handleCrearLotes}
            disabled={creandoLotes || !formularioValido}
            style={{borderRadius: '8px', minWidth: '250px'}}
            title={!formularioValido ? 'Hay magnitudes con cantidad de patrones incorrecta' : ''}
          >
            {creandoLotes ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando lotes para {(loteEquipos.equipos || []).length} equipos...
              </>
            ) : (
              <>
                <FaCheckCircle className="me-2" />
                Crear lotes y continuar
              </>
            )}
          </Button>

          {/* Mensajes de validación bajo el botón — se listan todas las
              magnitudes con conteo incorrecto. */}
          {!formularioValido && (
            <Alert variant="warning" className="mt-3 text-start" style={{maxWidth: '600px', margin: '1rem auto'}}>
              <strong>Ajusta los patrones antes de continuar:</strong>
              <ul className="mb-0 mt-1">
                {erroresPatrones.map((err, i) => (
                  <li key={i} className="small">{err}</li>
                ))}
              </ul>
            </Alert>
          )}
          <p className="text-muted mt-2 small">
            Se creará registros de calibración individual para cada equipo en la db
          </p>
        </div>
      )}

      {/* Modal para crear nueva plantilla */}
      <Modal 
        show={showModalCrearPlantilla} 
        onHide={() => setShowModalCrearPlantilla(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Crear Nueva Plantilla</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PlantillaForm 
            onSuccess={() => {
              setShowModalCrearPlantilla(false);
              cargarPlantillas();
            }}
            onCancel={() => setShowModalCrearPlantilla(false)}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StepSeleccionPlantilla;
